import { ProductBlock } from '@redneckz/wildless-cms-uni-blocks/lib/components/ProductBlock/ProductBlock';
import { useMobileMode } from '@redneckz/wildless-cms-uni-blocks/lib/hooks/useMobileMode';
import { TabsControl } from '@redneckz/wildless-cms-uni-blocks/lib/ui-kit/TabsControl/TabsControl';
import { style } from '@redneckz/wildless-cms-uni-blocks/lib/utils/style';
import { type TabItemBase } from '@redneckz/wildless-cms-uni-blocks/mobile/lib/ui-kit/TabsControl/TabItem';
import React from 'react';
import { NewsListItem } from './NewsListItem';
import { NewsRightBlock } from './NewsRightBlock';
import { normalizeImage } from './normalizeImage';
import { PREMIUM_ULTRA_TABS, TABS } from './tabs';

const { useLink } = process.env.GATSBY_MOBILE
  ? await import('@redneckz/wildless-cms-uni-blocks/mobile/lib/hooks/useLink')
  : await import('@redneckz/wildless-cms-uni-blocks/lib/hooks/useLink');

const { useActiveRoute } = process.env.GATSBY_MOBILE
  ? await import('@redneckz/wildless-cms-uni-blocks/mobile/lib/hooks/useActiveRoute')
  : await import('@redneckz/wildless-cms-uni-blocks/lib/hooks/useActiveRoute');

export const NewsSection = ({
  data,
  directions,
}: {
  data: Queries.Query;
  directions?: [string];
}) => {
  const link = useLink();

  const { pageTabs, activeTab, news, categories, hideCategories, isMobile } = useNewsSectionData({
    data,
    directions,
  });

  const handleTabChange = (tab: TabItemBase) => {
    if (tab.href) {
      const { onClick } = link(tab);
      onClick();
    }
  };

  return (
    <div className="grid grid-cols-12 gap-2xs">
      <ProductBlock
        className="col-span-12"
        title={data?.productBlockJson?.title as string}
        description={data?.productBlockJson?.description as string}
        image={normalizeImage(data?.productBlockJson?.image)}
        headlineVersion="XL"
        backwardButton
        padding={isMobile ? 'p-xl' : ''}
      />
      <div className="col-span-12 sm:col-span-9 space-y-px sm:space-y-2xs">
        <TabsControl
          items={pageTabs}
          activeItem={activeTab}
          onChange={handleTabChange}
          tabsType={isMobile ? 'underlined' : 'default'}
          className={style({ 'bg-white': isMobile })}
        />
        {!hideCategories && (
          <NewsRightBlock
            className="space-y-2xs flex-col sm:hidden flex"
            categories={categories}
            hideCategories={hideCategories}
            isFoldable={true}
          />
        )}
        {news.map(({ title, newsDate, direction, id, slug }) => (
          <NewsListItem
            key={id}
            newsDate={newsDate}
            isPremium={direction?.directionType === 'PremiumUltra'}
            slug={slug ?? ''}
            title={title}
          />
        ))}
      </div>
      <NewsRightBlock
        className="col-span-3 space-y-2xs flex-col hidden sm:flex"
        categories={categories}
        hideCategories={hideCategories}
      />
    </div>
  );
};

const useNewsSectionData = ({
  data,
  directions,
}: {
  data: Queries.Query;
  directions?: [string];
}) => {
  const isPremiumUltra = directions?.includes('PremiumUltra') ?? false;
  const pageTabs = isPremiumUltra ? PREMIUM_ULTRA_TABS : TABS;

  const [, activeTab] = useActiveRoute(pageTabs);
  const news = data?.allContentJson?.nodes ?? [];
  const categories = [...(data?.allPortalResourcesJson?.nodes?.[0]?.items ?? [])];
  const hideCategories = isPremiumUltra || activeTab === pageTabs[1];
  const isMobile = useMobileMode();

  return { pageTabs, activeTab, news, categories, hideCategories, isMobile };
};
